import React, { useState, useEffect, useCallback } from "react";
import {
  Calendar,
  Search,
  RefreshCw,
  CheckCircle,
  Clock,
  TrendingUp,
  Phone,
  Mail,
  DollarSign,
  Building2,
  User,
  ExternalLink,
  Eye,
  Trash2,
  Heart,
  Inbox,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
} from "lucide-react";
import apiService from "../../services/api";
import LeadDetailsModal from "../../components/LeadDetailsModal";
import BookAppointmentModal from "../../components/BookAppointmentModal";
import ConvertLeadModal from "../../components/ConvertLeadModal";
import { useNavigate } from "react-router-dom";
import LeadSearchInput from "../../components/LeadSearchInput";
import { useRealtimeSync } from "../../hooks/useRealtimeSync";
import tokenManager from "../../utils/tokenManager";
import PhoneLink from "../../components/PhoneLink";
import { useTheme } from "../../contexts/ThemeContext";

const MyLeads = () => {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const [todaysLeads, setTodaysLeads] = useState([]);
  const [callbackLeads, setCallbackLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);
  const [showBookAppointment, setShowBookAppointment] = useState(null);
  const [showConvertModal, setShowConvertModal] = useState(null);
  const [stats, setStats] = useState({
    todayTasks: 0,
    callbacks: 0,
  });
  const [view, setView] = useState("today");
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [sendingToLeadPot, setSendingToLeadPot] = useState(false);
  const [showSendAllConfirmation, setShowSendAllConfirmation] = useState(false);
  const [dailyCallbackFilter, setDailyCallbackFilter] = useState(null);
  const [callbackFilterActive, setCallbackFilterActive] = useState(false);

  // Search state
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredTodaysLeads, setFilteredTodaysLeads] = useState([]);
  const [filteredCallbackLeads, setFilteredCallbackLeads] = useState([]);

  // Sort state
  const [sortConfig, setSortConfig] = useState({
    field: null,
    direction: "asc",
  });

  // Pagination state for callbacks
  const [callbackPage, setCallbackPage] = useState(1);
  const [callbackTotalCount, setCallbackTotalCount] = useState(0);
  const CALLBACKS_PER_PAGE = 50;

  const handleRealtimeUpdate = useCallback(
    (data) => {
      console.log("📡 Received real-time lead update:", data);

      if (data.type === "lead_updated") {
        const { action, data: leadData } = data;
        const currentUserId = tokenManager.getUser()?.id;
        const isMyLead = leadData.assignedTo === currentUserId;

        const isTodayTask = (lead) => {
          if (lead.leadStatus !== "new") return false;
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const assignedDate = new Date(lead.assignedAt);
          assignedDate.setHours(0, 0, 0, 0);
          return assignedDate.getTime() === today.getTime();
        };

        const isCallback = (lead) => lead.leadStatus === "nurture";

        setTodaysLeads((prev) => {
          if (!isMyLead || action === "deleted" || !isTodayTask(leadData)) {
            return prev.filter((lead) => lead.id !== leadData.id);
          }

          if (action === "created" || action === "assigned") {
            const exists = prev.some((l) => l.id === leadData.id);
            if (!exists) {
              return [leadData, ...prev];
            }
          }

          if (action === "updated") {
            return prev.map((lead) =>
              lead.id === leadData.id ? { ...lead, ...leadData } : lead,
            );
          }

          return prev;
        });

        setCallbackLeads((prev) => {
          if (!isMyLead || action === "deleted" || !isCallback(leadData)) {
            return prev.filter((lead) => lead.id !== leadData.id);
          }

          if (
            action === "created" ||
            action === "assigned" ||
            action === "updated"
          ) {
            const exists = prev.some((l) => l.id === leadData.id);
            if (!exists && isCallback(leadData)) {
              return [leadData, ...prev];
            }
            return prev.map((lead) =>
              lead.id === leadData.id ? { ...lead, ...leadData } : lead,
            );
          }

          return prev;
        });

        calculateStats();
      }
    },
    [todaysLeads, callbackLeads],
  );

  const { isConnected } = useRealtimeSync({
    events: ["lead_updated"],
    onUpdate: handleRealtimeUpdate,
    enabled: true,
  });

  useEffect(() => {
    apiService.warmupLeadCache().catch(console.error);
  }, []);

  useEffect(() => {
    const loadDailyFilter = async () => {
      const filter = await apiService.getActiveDailyCallbackFilter();
      setDailyCallbackFilter(filter);
      setCallbackFilterActive(!!filter);
    };
    loadDailyFilter();
  }, []);

  useEffect(() => {
    fetchLeads();
  }, [callbackPage, dailyCallbackFilter]);

  useEffect(() => {
    if (!searchQuery || searchQuery.length < 2) {
      setFilteredTodaysLeads(todaysLeads);
      setFilteredCallbackLeads(callbackLeads);
      return;
    }

    const query = searchQuery.toLowerCase().trim();

    const filterLeads = (leads) =>
      leads.filter((lead) => {
        const fullName = lead.fullName?.toLowerCase() || "";
        const email = lead.email?.toLowerCase() || "";
        const phone = lead.phone?.toLowerCase() || "";
        const companyName = lead.companyName?.toLowerCase() || "";

        return (
          fullName.includes(query) ||
          email.includes(query) ||
          phone.includes(query) ||
          companyName.includes(query)
        );
      });

    setFilteredTodaysLeads(filterLeads(todaysLeads));
    setFilteredCallbackLeads(filterLeads(callbackLeads));
  }, [searchQuery, todaysLeads, callbackLeads]);

  const fetchLeads = async () => {
    try {
      setLoading(true);

      const todayResponse = await apiService.getTodaysLeads();
      const callbackResponse = await apiService.getMyLeads({
        leadStatus: "nurture",
        page: callbackPage,
        limit: CALLBACKS_PER_PAGE,
        ...(dailyCallbackFilter
          ? { advancedFilters: JSON.stringify(dailyCallbackFilter) }
          : {}),
      });

      setTodaysLeads(todayResponse.data || []);
      setCallbackLeads(callbackResponse.data || []);

      if (callbackResponse.pagination) {
        setCallbackTotalCount(callbackResponse.pagination.total || 0);
      }

      calculateStats(
        todayResponse.data,
        callbackResponse.pagination?.total ||
          callbackResponse.data?.length ||
          0,
      );
    } catch (error) {
      console.error("Error fetching leads:", error);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (
    today = todaysLeads,
    callbacksCount = callbackTotalCount,
  ) => {
    setStats({
      todayTasks: Array.isArray(today) ? today.length : 0,
      callbacks:
        typeof callbacksCount === "number"
          ? callbacksCount
          : callbackLeads.length,
    });
  };

  const handleUpdateLead = async (leadId, data) => {
    try {
      await apiService.updateLead(leadId, data);

      if (data.leadStatus && data.leadStatus !== "new") {
        setTodaysLeads((prev) => prev.filter((lead) => lead.id !== leadId));
      }
      if (data.leadStatus && data.leadStatus !== "nurture") {
        setCallbackLeads((prev) => prev.filter((lead) => lead.id !== leadId));
      }

      await fetchLeads();
    } catch (error) {
      console.error("Error updating lead:", error);
    }
  };

  const handleDeleteLead = async (leadId) => {
    if (window.confirm("Are you sure you want to delete this lead?")) {
      try {
        await apiService.deleteLead(leadId);
        await fetchLeads();
        setSelectedLead(null);
      } catch (error) {
        console.error("Error deleting lead:", error);
      }
    }
  };

  const handleSelectLead = (leadId) => {
    setSelectedLeads((prev) => {
      if (prev.includes(leadId)) {
        return prev.filter((id) => id !== leadId);
      } else {
        return [...prev, leadId];
      }
    });
  };

  const handleSelectAll = () => {
    const currentLeads =
      view === "today" ? filteredTodaysLeads : filteredCallbackLeads;
    if (selectedLeads.length === currentLeads.length) {
      setSelectedLeads([]);
    } else {
      setSelectedLeads(currentLeads.map((lead) => lead.id));
    }
  };

  const handleSendAllToLeadPot = async () => {
    if (todaysLeads.length === 0) {
      alert("No leads to send to Lead Pot");
      return;
    }
    setShowSendAllConfirmation(true);
  };

  const confirmSendAllToLeadPot = async () => {
    setShowSendAllConfirmation(false);

    try {
      setSendingToLeadPot(true);

      const updatePromises = todaysLeads.map((lead) =>
        apiService.updateLead(lead.id, { leadStatus: "lead_pot" }),
      );

      await Promise.all(updatePromises);

      setTodaysLeads([]);
      setSelectedLeads([]);

      await fetchLeads();

      alert(`Successfully sent ${todaysLeads.length} leads to the Lead Pot`);
    } catch (error) {
      console.error("Error sending leads to Lead Pot:", error);
      alert("Failed to send leads to Lead Pot. Please try again.");
      await fetchLeads();
    } finally {
      setSendingToLeadPot(false);
    }
  };

  const handleSort = (field) => {
    setSortConfig((prev) => {
      if (prev.field === field) {
        return {
          field,
          direction: prev.direction === "asc" ? "desc" : "asc",
        };
      }
      return {
        field,
        direction: "asc",
      };
    });
  };

  const getSortedLeads = (leads) => {
    if (!sortConfig.field) return leads;

    return [...leads].sort((a, b) => {
      let aValue = a[sortConfig.field];
      let bValue = b[sortConfig.field];

      if (aValue == null) aValue = "";
      if (bValue == null) bValue = "";

      if (typeof aValue === "string") aValue = aValue.toLowerCase();
      if (typeof bValue === "string") bValue = bValue.toLowerCase();

      if (aValue < bValue) {
        return sortConfig.direction === "asc" ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === "asc" ? 1 : -1;
      }
      return 0;
    });
  };

  const getSortIcon = (field) => {
    if (sortConfig.field !== field) {
      return <ArrowUpDown size={14} className="text-gray-500" />;
    }
    return sortConfig.direction === "asc" ? (
      <ArrowUp size={14} className="text-blue-400" />
    ) : (
      <ArrowDown size={14} className="text-blue-400" />
    );
  };

  const getStatusColor = (status) => {
    const colors = {
      new: `text-blue-400 ${isDarkMode ? "bg-[#0C0E12]" : "bg-white"}`,
      contacted: `text-orange-400 ${isDarkMode ? "bg-[#0C0E12]" : "bg-white"}`,
      qualified: `text-green-400 ${isDarkMode ? "bg-[#0C0E12]" : "bg-white"}`,
      unqualified: `text-gray-400 ${isDarkMode ? "bg-[#0C0E12]" : "bg-white"}`,
      converted: `text-purple-400 ${isDarkMode ? "bg-[#0C0E12]" : "bg-white"}`,
      lost: `text-red-400 ${isDarkMode ? "bg-[#0C0E12]" : "bg-white"}`,
      nurture: `text-pink-400 ${isDarkMode ? "bg-[#0C0E12]" : "bg-white"}`,
    };
    return (
      colors[status] ||
      `text-gray-400 ${isDarkMode ? "bg-[#0C0E12]" : "bg-white"}`
    );
  };

  const StatCard = ({ title, value, icon: Icon }) => (
    <div
      className={`rounded-xl p-6 border transition-colors ${isDarkMode ? "bg-[#0C0E12] border-gray-700/50" : "bg-white border-gray-200"}`}
    >
      <div className="flex items-center justify-between mb-2">
        <div
          className={`text-sm font-medium ${isDarkMode ? "text-[#94979C]" : "text-gray-500"}`}
        >
          {title}
        </div>
        {Icon && <Icon size={20} className="text-gray-400" />}
      </div>
      <div
        className={`text-3xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}
      >
        {value}
      </div>
    </div>
  );

  const LeadRow = ({ lead }) => {
    return (
      <tr
        className={`border-b transition-colors ${isDarkMode ? "border-gray-700 hover:bg-gray-800/50" : "border-gray-200 hover:bg-gray-50"}`}
      >
        <td className="py-4 px-4">
          <input
            type="checkbox"
            checked={selectedLeads.includes(lead.id)}
            onChange={() => handleSelectLead(lead.id)}
            className={`w-4 h-4 rounded text-blue-600 focus:ring-blue-500 ${isDarkMode ? "border-gray-700 bg-gray-800" : "border-gray-300 bg-white"}`}
          />
        </td>
        <td className="py-4 px-4">
          <button
            onClick={() => {
              if (view === "callbacks") {
                window.open(`/leads/${lead.id}`, "_blank");
              } else {
                navigate(`/leads/${lead.id}`);
              }
            }}
            className={`font-medium hover:text-blue-400 transition-colors ${isDarkMode ? "text-white" : "text-gray-900"}`}
          >
            {lead.companyName || "N/A"}
          </button>
        </td>
        <td className="py-4 px-4">
          <div>
            <div
              className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
            >
              {lead.fullName || "N/A"}
            </div>
            {lead.email && (
              <div
                className={`text-sm flex items-center gap-1 mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
              >
                <Mail size={12} />
                {lead.email}
              </div>
            )}
          </div>
        </td>
        <td className="py-4 px-4">
          {lead.phone ? (
            <PhoneLink
              phoneNumber={lead.phone}
              entityType="lead"
              entityId={lead.id}
              entityName={lead.fullName || lead.companyName}
              className="hover:underline"
            />
          ) : (
            <span className={isDarkMode ? "text-gray-400" : "text-gray-500"}>
              N/A
            </span>
          )}
        </td>
        <td className="py-4 px-4">
          <span
            className={`inline-flex items-center px-2 py-1 rounded-full border text-xs font-medium ${isDarkMode ? "border-[#373A41]" : "border-gray-300"} ${getStatusColor(lead.leadStatus)}`}
          >
            <div className="w-2 h-2 rounded-full bg-current mr-2"></div>
            {lead.leadStatus === "nurture" ? "Callback" : lead.leadStatus}
          </span>
        </td>

        <td className="py-4 px-4">
          {lead.isConverted ? (
            <div className="flex items-center gap-2">
              <CheckCircle size={16} className="text-purple-400" />
              <div className="text-xs">
                <div className="text-purple-400 font-medium">Converted</div>
                {lead.convertedAccountId && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/accounts/${lead.convertedAccountId}`);
                    }}
                    className={`flex items-center gap-1 hover:text-blue-400 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    <Building2 size={10} />
                    View Account
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowBookAppointment(lead)}
                className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded transition-colors"
              >
                Schedule
              </button>
              {lead.qualificationStatus === "qualified" && (
                <button
                  onClick={() => setShowConvertModal(lead)}
                  className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-xs rounded transition-colors flex items-center gap-1"
                >
                  <DollarSign size={12} />
                  Convert
                </button>
              )}
            </div>
          )}
        </td>
        <td className="py-4 px-4 text-right">
          <div className="flex items-center gap-2 justify-end">
            <button
              onClick={() => {
                if (view === "callbacks") {
                  window.open(`/leads/${lead.id}`, "_blank");
                } else {
                  navigate(`/leads/${lead.id}`);
                }
              }}
              className={`transition-colors p-1 ${isDarkMode ? "text-gray-400 hover:text-white" : "text-gray-400 hover:text-gray-700"}`}
              title="View Details"
            >
              <Eye size={16} />
            </button>
            <button
              onClick={() => handleDeleteLead(lead.id)}
              className="text-gray-400 hover:text-red-400 transition-colors p-1"
              title="Delete"
            >
              <Trash2 size={16} />
            </button>
          </div>
        </td>
      </tr>
    );
  };

  const displayLeads =
    view === "today"
      ? getSortedLeads(filteredTodaysLeads)
      : getSortedLeads(filteredCallbackLeads);

  return (
    <div
      className={`min-h-screen font-inter ${isDarkMode ? "bg-[#0C0E12] text-white" : "bg-gray-50 text-gray-900"}`}
    >
      <div className="p-4 lg:p-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1
              className={`text-2xl sm:text-3xl lg:text-4xl font-bold mb-1 ${isDarkMode ? "text-white" : "text-gray-900"}`}
            >
              My Tasks
            </h1>
            <p
              className={`text-sm sm:text-base ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
            >
              Manage your daily tasks and callbacks
            </p>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={fetchLeads}
              className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${isDarkMode ? "bg-[#0C0E12] border-gray-700 text-gray-300 hover:bg-gray-700" : "bg-white border-gray-300 text-gray-600 hover:bg-gray-50"}`}
            >
              <RefreshCw size={16} />
              Refresh
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <StatCard
            title="Today's Tasks"
            value={stats.todayTasks}
            icon={Clock}
          />
          <StatCard title="Callbacks" value={stats.callbacks} icon={Heart} />
        </div>

        {/* View Toggle */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={() => {
                setView("today");
                setSearchQuery("");
                setSortConfig({ field: null, direction: "asc" });
              }}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                view === "today"
                  ? "bg-blue-600 text-white"
                  : isDarkMode
                    ? "bg-[#0C0E12] border border-gray-700 text-gray-300 hover:bg-gray-700"
                    : "bg-white border border-gray-300 text-gray-600 hover:bg-gray-50"
              }`}
            >
              <Clock size={16} />
              Today's Tasks ({stats.todayTasks})
            </button>
            <button
              onClick={() => {
                setView("callbacks");
                setSearchQuery("");
                setSortConfig({ field: null, direction: "asc" });
              }}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                view === "callbacks"
                  ? "bg-blue-600 text-white"
                  : isDarkMode
                    ? "bg-[#0C0E12] border border-gray-700 text-gray-300 hover:bg-gray-700"
                    : "bg-white border border-gray-300 text-gray-600 hover:bg-gray-50"
              }`}
            >
              <Heart size={16} />
              Callbacks ({stats.callbacks})
            </button>
          </div>

          {view === "today" && todaysLeads.length > 0 && (
            <button
              onClick={handleSendAllToLeadPot}
              disabled={sendingToLeadPot}
              className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 text-white rounded-lg font-medium transition-all duration-300 relative hover:scale-[1.02] hover:shadow-xl text-sm sm:text-base min-w-0"
              style={{
                background:
                  "radial-gradient(50% 50% at 50% 50%, #427BCA 0%, #2F578F 100%)",
                border: "1px solid",
                borderImageSource:
                  "radial-gradient(50% 50% at 50% 50%, #FFFFFF 0%, rgba(255, 255, 255, 0) 100%)",
                boxShadow: `
                  0px 36px 32px -16px #427BCA36,
                  0px 8px 16px 0px #2F578F1F,
                  0px 2px 4px -8px #427BCA14,
                  0px 8px 24px -16px #FFFFFF3D inset,
                  0px -24px 32px 0px #FFFFFF38 inset,
                  0px 0px 12px 0px #FFFFFF inset
                `,
              }}
            >
              <Inbox size={16} />
              {sendingToLeadPot
                ? "Sending..."
                : `Send All to Lead Pot (${todaysLeads.length})`}
            </button>
          )}
        </div>

        {/* Search Bar */}
        <div className="mb-6">
          <LeadSearchInput
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder={`Search ${view === "today" ? "today's tasks" : "callbacks"}...`}
          />
          {searchQuery && (
            <p
              className={`text-sm mt-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
            >
              Found {displayLeads.length} result
              {displayLeads.length !== 1 ? "s" : ""}
            </p>
          )}
        </div>

        {/* Bulk Actions Bar */}
        {selectedLeads.length > 0 && (
          <div className="mb-4 bg-blue-600/10 border border-blue-600/30 rounded-lg p-4 flex items-center justify-between">
            <span className="text-blue-400 font-medium">
              {selectedLeads.length} lead{selectedLeads.length !== 1 ? "s" : ""}{" "}
              selected
            </span>
            <button
              onClick={() => setSelectedLeads([])}
              className={`px-4 py-2 rounded-lg transition-colors ${isDarkMode ? "bg-gray-700 text-gray-300 hover:bg-gray-600" : "bg-gray-200 text-gray-600 hover:bg-gray-300"}`}
            >
              Clear Selection
            </button>
          </div>
        )}

        {/* Table */}
        <div
          className={`rounded-lg border mb-8 ${isDarkMode ? "bg-[#0C0E12] border-gray-700" : "bg-white border-gray-200"}`}
        >
          <div
            className={`flex items-center justify-between p-6 border-b ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
          >
            <div>
              <h2
                className={`text-xl font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                {view === "today" ? "Today's Tasks" : "Callbacks"}{" "}
                <span
                  className={`text-sm ml-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                >
                  {displayLeads.length} leads
                </span>
              </h2>
              <p
                className={`text-sm mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
              >
                {view === "today"
                  ? "New leads assigned to you today"
                  : "Leads marked for callback"}
              </p>
            </div>
          </div>

          <div className="overflow-x-auto">
            {loading ? (
              <div
                className={`text-center py-12 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
              >
                Loading leads...
              </div>
            ) : displayLeads.length === 0 ? (
              <div className="text-center py-12">
                <Clock size={48} className="mx-auto mb-4 text-gray-600" />
                <p className={isDarkMode ? "text-gray-400" : "text-gray-500"}>
                  {searchQuery
                    ? "No leads found matching your search"
                    : view === "today"
                      ? "No tasks for today"
                      : "No callbacks scheduled"}
                </p>
              </div>
            ) : (
              <table className="w-full min-w-[1000px]">
                <thead className={isDarkMode ? "bg-[#13161B]" : "bg-gray-50"}>
                  <tr
                    className={`border-b ${isDarkMode ? "border-[#22262F]" : "border-gray-200"}`}
                  >
                    <th className="text-left py-4 px-4 w-12">
                      <input
                        type="checkbox"
                        checked={
                          selectedLeads.length === displayLeads.length &&
                          displayLeads.length > 0
                        }
                        onChange={handleSelectAll}
                        className={`w-4 h-4 rounded text-blue-600 focus:ring-blue-500 ${isDarkMode ? "border-gray-700 bg-gray-800" : "border-gray-300 bg-white"}`}
                      />
                    </th>
                    <th
                      className={`text-left py-4 px-4 font-medium text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      <button
                        onClick={() => handleSort("companyName")}
                        className={`flex items-center gap-2 transition-colors ${isDarkMode ? "hover:text-white" : "hover:text-gray-900"}`}
                      >
                        Company
                        {getSortIcon("companyName")}
                      </button>
                    </th>
                    <th
                      className={`text-left py-4 px-4 font-medium text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      <button
                        onClick={() => handleSort("fullName")}
                        className={`flex items-center gap-2 transition-colors ${isDarkMode ? "hover:text-white" : "hover:text-gray-900"}`}
                      >
                        Contact
                        {getSortIcon("fullName")}
                      </button>
                    </th>
                    <th
                      className={`text-left py-4 px-4 font-medium text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      Phone
                    </th>
                    <th
                      className={`text-left py-4 px-4 font-medium text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      <button
                        onClick={() => handleSort("leadStatus")}
                        className={`flex items-center gap-2 transition-colors ${isDarkMode ? "hover:text-white" : "hover:text-gray-900"}`}
                      >
                        Status
                        {getSortIcon("leadStatus")}
                      </button>
                    </th>
                    <th
                      className={`text-left py-4 px-4 font-medium text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      Actions
                    </th>
                    <th
                      className={`text-right py-4 px-4 font-medium text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      More
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {displayLeads.map((lead) => (
                    <LeadRow key={lead.id} lead={lead} />
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Pagination for callbacks */}
          {view === "callbacks" &&
            !loading &&
            callbackLeads.length > 0 &&
            callbackTotalCount > CALLBACKS_PER_PAGE && (
              <div
                className={`border-t px-6 py-4 flex items-center justify-between ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
              >
                <div
                  className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                >
                  Showing {(callbackPage - 1) * CALLBACKS_PER_PAGE + 1} to{" "}
                  {Math.min(
                    callbackPage * CALLBACKS_PER_PAGE,
                    callbackTotalCount,
                  )}{" "}
                  of {callbackTotalCount}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setCallbackPage((p) => Math.max(1, p - 1))}
                    disabled={callbackPage === 1}
                    className={`px-3 py-1.5 text-sm rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${isDarkMode ? "bg-gray-800 text-gray-300 hover:bg-gray-700" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setCallbackPage((p) => p + 1)}
                    disabled={
                      callbackPage * CALLBACKS_PER_PAGE >= callbackTotalCount
                    }
                    className={`px-3 py-1.5 text-sm rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${isDarkMode ? "bg-gray-800 text-gray-300 hover:bg-gray-700" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
        </div>
      </div>

      {/* Modals */}
      {selectedLead && (
        <LeadDetailsModal
          lead={selectedLead}
          onClose={() => setSelectedLead(null)}
          onUpdate={handleUpdateLead}
          onDelete={handleDeleteLead}
        />
      )}

      {showBookAppointment && (
        <BookAppointmentModal
          lead={showBookAppointment}
          onClose={() => setShowBookAppointment(null)}
          onSuccess={() => {
            setShowBookAppointment(null);
            fetchLeads();
          }}
        />
      )}

      {showConvertModal && (
        <ConvertLeadModal
          lead={showConvertModal}
          onClose={() => setShowConvertModal(null)}
          onSuccess={() => {
            setShowConvertModal(null);
            fetchLeads();
          }}
        />
      )}

      {/* Send All to Lead Pot Confirmation Modal */}
      {showSendAllConfirmation && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div
            className={`rounded-lg border max-w-md w-full ${isDarkMode ? "bg-[#0C0E12] border-gray-700" : "bg-white border-gray-200"}`}
          >
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-blue-600/10 flex items-center justify-center">
                  <Inbox size={24} className="text-blue-500" />
                </div>
                <h2
                  className={`text-xl font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  Send All to Lead Pot?
                </h2>
              </div>

              <p
                className={`mb-2 ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
              >
                You're about to send{" "}
                <span
                  className={`font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  {todaysLeads.length} lead{todaysLeads.length !== 1 ? "s" : ""}
                </span>{" "}
                to the Lead Pot.
              </p>

              <p
                className={`text-sm mb-6 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
              >
                These leads will be moved from your today's tasks and made
                available in the Lead Pot for the team.
              </p>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowSendAllConfirmation(false)}
                  className={`flex-1 px-4 py-2.5 rounded-lg font-medium transition-colors ${isDarkMode ? "bg-gray-700 text-white hover:bg-gray-600" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmSendAllToLeadPot}
                  className="flex-1 px-4 py-2.5 text-white rounded-lg font-medium transition-all duration-300 hover:scale-[1.02] hover:shadow-xl"
                  style={{
                    background:
                      "radial-gradient(50% 50% at 50% 50%, #427BCA 0%, #2F578F 100%)",
                    border: "1px solid",
                    borderImageSource:
                      "radial-gradient(50% 50% at 50% 50%, #FFFFFF 0%, rgba(255, 255, 255, 0) 100%)",
                    boxShadow: `
                      0px 36px 32px -16px #427BCA36,
                      0px 8px 16px 0px #2F578F1F,
                      0px 2px 4px -8px #427BCA14,
                      0px 8px 24px -16px #FFFFFF3D inset,
                      0px -24px 32px 0px #FFFFFF38 inset,
                      0px 0px 12px 0px #FFFFFF inset
                    `,
                  }}
                >
                  Send to Lead Pot
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyLeads;
